import { useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useExternalProviderRepository } from "../context/external-provider-repository.context";
import { useToast } from "@components/ui/use-toast";
import type { AppState } from "@redux/app-state";
import type { RequestState } from "@redux/state/shared-types";
import type { SubmitEvaluationBody } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import {
  submitExternalProviderEvaluation,
  resetSubmitExternalProviderEvaluation,
} from "@academy-context/write/application/use-cases/external-provider/evaluation-submission/submit-evaluation";

const selectExternalProviderEvaluationSubmission = (state: AppState) => state.externalProvider.evaluationSubmission;

interface UseEvaluationSubmitResult {
  submit: (participationId: string, body: SubmitEvaluationBody) => Promise<boolean>;
  processing: RequestState;
}

export const useEvaluationSubmit = (onSuccess: () => void): UseEvaluationSubmitResult => {
  const dispatch = useAppDispatch();
  const repository = useExternalProviderRepository();
  const { toast } = useToast();
  const { processing } = useAppSelector(selectExternalProviderEvaluationSubmission);

  useEffect(() => {
    if (processing === "success") {
      toast({ description: "Évaluation soumise avec succès !" });
      onSuccess();
      dispatch(resetSubmitExternalProviderEvaluation());
    } else if (processing === "failed") {
      toast({
        description: "Erreur lors de la soumission de l'évaluation",
        variant: "destructive",
      });
      dispatch(resetSubmitExternalProviderEvaluation());
    }
  }, [dispatch, processing, toast, onSuccess]);

  const submit = useCallback(
    async (participationId: string, body: SubmitEvaluationBody): Promise<boolean> => {
      try {
        await dispatch(
          submitExternalProviderEvaluation({
            participationId,
            body,
            externalProviderRepository: repository,
          }),
        ).unwrap();
        return true;
      } catch {
        return false;
      }
    },
    [dispatch, repository],
  );

  return { submit, processing };
};
